"use client";
import { useEffect, useState } from "react";
import Link from "next/link";
import { X } from "lucide-react";
import { usePathname } from "next/navigation";
import { createClient } from "@/utils/supabase/client";

const normalizeLinkUrl = (url: string) => {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return trimmed;
  return `/${trimmed.replace(/^\/+/, '')}`;
};

type Banner = {
  id: string;
  message: string;
  link_text: string | null;
  link_url: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
};

export default function CustomBanner() {
  const [closed, setClosed] = useState(true);
  const [closedPath, setClosedPath] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [top, setTop] = useState(0);
  const pathname = usePathname();

  // Fetch the latest active banner
  useEffect(() => {
    const fetchBanner = async () => {
      const supabase = createClient();
      const { data, error } = await supabase
        .from("banners")
        .select("*")
        .eq("active", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Error fetching banner:", error);
        return;
      }

      if (data) {
        setBanner(data);
      }
    };

    fetchBanner();
    setHydrated(true);
  }, []);

  // Re-open on route change (page-level visibility) unless user already closed on this path
  useEffect(() => {
    if (!hydrated) return;
    if (!banner) return;
    if (closedPath === pathname) return; // user closed for this path, respect it
    setClosed(false);
  }, [pathname, hydrated, banner, closedPath]);

  // Measure header + subheader heights and update top & exposed vars
  useEffect(() => {
    if (!hydrated) return;

    let raf = 0;
    const computeNow = () => {
      // Sur la page feed, les banners sont en overlay et se positionnent juste en dessous du feed-top-bar
      const isFeedPage = pathname === '/feed';
      
      if (isFeedPage) {
        // Sur la page feed, le feed-top-bar fait environ 56px de hauteur (py-3 + contenu)
        // Les banners se positionnent juste en dessous
        const feedTopBar = document.querySelector('[data-feed-top-bar]') as HTMLElement | null;
        const feedTopBarHeight = feedTopBar?.offsetHeight || 56;
        // Check if contest banner is visible and get its height
        const contestBannerEl = document.getElementById("kraahl-contest-banner");
        const contestBannerH = contestBannerEl && getComputedStyle(contestBannerEl).display !== 'none' 
          ? contestBannerEl.offsetHeight 
          : 0;
        setTop(feedTopBarHeight + contestBannerH);
        return;
      }

      const readCssPx = (name: string, fallback: number) => {
        const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
        const n = Number.parseFloat(raw);
        return Number.isFinite(n) ? n : fallback;
      };
      const headerH = readCssPx("--header-height", 0);
      const contestBannerEl = document.getElementById("kraahl-contest-banner");
      const contestBannerH =
        contestBannerEl && getComputedStyle(contestBannerEl).display !== "none"
          ? contestBannerEl.offsetHeight
          : 0;
      setTop(Math.max(0, headerH + contestBannerH));
    };

    const scheduleCompute = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(computeNow);
    };

    scheduleCompute();

    const ro = new ResizeObserver(scheduleCompute);
    const headerEl = document.querySelector('header') as HTMLElement | null;
    headerEl && ro.observe(headerEl);
    const contestBannerEl = document.getElementById("kraahl-contest-banner");
    contestBannerEl && ro.observe(contestBannerEl);

    window.addEventListener("resize", scheduleCompute);
    window.addEventListener("scroll", scheduleCompute, { passive: true });

    const mo = new MutationObserver(() => {
      scheduleCompute();
    });
    mo.observe(document.body, { subtree: true, childList: true, attributes: true });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", scheduleCompute);
      window.removeEventListener("scroll", scheduleCompute);
      if (raf) cancelAnimationFrame(raf);
      mo.disconnect();
    };
  }, [hydrated, pathname]);

  // Expose banner height variable when visible (for padding of content)
  // Sur la page feed, les banners sont en overlay et ne doivent pas affecter le layout
  useEffect(() => {
    if (!hydrated) return;
    const el = document.getElementById("kraahl-custom-banner");
    const update = () => {
      const elNow = document.getElementById("kraahl-custom-banner");
      // Sur la page feed, on ne définit pas la variable car les banners sont en overlay
      if (pathname === '/feed') {
        document.documentElement.style.removeProperty("--custom-banner-height");
        return;
      }
      if (elNow && !closed && banner) {
        document.documentElement.style.setProperty(
          "--custom-banner-height",
          elNow.offsetHeight + "px"
        );
      } else {
        document.documentElement.style.removeProperty("--custom-banner-height");
      }
    };

    // Run once immediately to set or remove the variable
    update();

    // If banner visible, observe size changes and window resizes (mobile address bar changes trigger resize)
    let ro: ResizeObserver | null = null;
    if (el && !closed && banner) {
      ro = new ResizeObserver(update);
      ro.observe(el);
      window.addEventListener("resize", update);
      window.addEventListener("orientationchange", update);
      // Listen for CSS transitions that may change height
      el.addEventListener("transitionend", update);
    }

    return () => {
      ro && ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      el && el.removeEventListener("transitionend", update);
      // Ensure variable is removed when component unmounts/hidden
      if (!document.getElementById("kraahl-custom-banner")) {
        document.documentElement.style.removeProperty("--custom-banner-height");
      }
    };
  }, [hydrated, closed, banner, pathname]);

  if (!hydrated || !banner || closed) return null;

  // Render message with link if link_text and link_url are provided
  const renderMessage = () => {
    if (!banner.link_text || !banner.link_url) {
      return <span>{banner.message}</span>;
    }

    // Find the first occurrence of link_text in the message
    const linkIndex = banner.message.indexOf(banner.link_text);
    if (linkIndex === -1) {
      // link_text not found in message, just display message
      return <span>{banner.message}</span>;
    }

    // Split message into parts: before link, link text, after link
    const beforeLink = banner.message.substring(0, linkIndex);
    const afterLink = banner.message.substring(linkIndex + banner.link_text.length);

    const normalizedLinkUrl = normalizeLinkUrl(banner.link_url);
    if (!normalizedLinkUrl) {
      return <span>{banner.message}</span>;
    }
    const isInternal = normalizedLinkUrl.startsWith('/') || normalizedLinkUrl.startsWith('#');
    
    if (isInternal) {
      return (
        <>
          {beforeLink}
          <Link href={normalizedLinkUrl} className="underline underline-offset-2 font-semibold hover:opacity-90">
            {banner.link_text}
          </Link>
          {afterLink}
        </>
      );
    } else {
      return (
        <>
          {beforeLink}
          <a 
            href={normalizedLinkUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="underline underline-offset-2 font-semibold hover:opacity-90"
          >
            {banner.link_text}
          </a>
          {afterLink}
        </>
      );
    }
  };

  return (
    <div
      id="kraahl-custom-banner"
      className="fixed left-0 right-0 z-41 bg-foreground text-background dark:bg-foreground dark:text-background border-b border-border/30 shadow-xs backdrop-blur-xs transition-all duration-300 ease-out"
      style={{ top, willChange: 'top' }}
      role="status"
      aria-label="Custom banner message"
    >
      <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-2 text-[11px] sm:text-[13px] font-medium leading-snug">
        <span className="inline-flex items-center rounded-full bg-background/25 text-background px-2 py-0.5 text-[9px] sm:text-[10px] tracking-wide uppercase font-semibold shadow-xs">Live</span>
        <p className="flex-1 text-[11px] sm:text-[13px] font-medium">
          {renderMessage()}
        </p>
        <button
          aria-label="Close custom banner"
          onClick={() => {
            setClosedPath(pathname);
            setClosed(true);
          }}
          className="shrink-0 rounded-full p-1.5 hover:bg-background/25 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-background/60 transition"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

